import { useEffect, useState } from 'react';

const AUTH_URL = 'https://auth.redwoodoperations.com';
const SERVICE_PREFIX = 'docsend:';

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  groups: string[];
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch {
    return null;
  }
}

interface TokenState {
  status: 'none' | 'valid' | 'no-access';
  email: string | null;
  user: AuthUser | null;
}

function readToken(): TokenState {
  const token = getCookie('rw_token');
  if (!token) return { status: 'none', email: null, user: null };
  const payload = parseJwtPayload(token);
  if (!payload) return { status: 'none', email: null, user: null };
  const exp = payload.exp as number;
  if (exp && exp * 1000 < Date.now()) return { status: 'none', email: null, user: null };
  const email = (payload.email as string) ?? null;
  const groups = (payload['cognito:groups'] as string[]) ?? [];
  if (!groups.some((g) => g.startsWith(SERVICE_PREFIX))) {
    return { status: 'no-access', email, user: null };
  }
  return {
    status: 'valid',
    email,
    user: {
      sub: payload.sub as string,
      email: email ?? '',
      name: (payload.name as string) ?? email ?? '',
      groups,
    },
  };
}

function redirectToLogin(): void {
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `${AUTH_URL}?redirect=${redirect}`;
}

export function signOut(): void {
  // Best-effort cookie clear; /logout on auth-service is the source of truth
  // (it calls Amplify signOut(), which actually clears the localStorage session).
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `rw_token=; path=/; ${expire}`;
  document.cookie = `rw_token=; domain=redwoodoperations.com; path=/; ${expire}`;
  document.cookie = `rw_token=; domain=.redwoodoperations.com; path=/; ${expire}`;
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `${AUTH_URL}/logout?redirect=${redirect}`;
}

export type UseAuthResult =
  | { loading: true; user: null; noAccess: false; email: null }
  | { loading: false; user: AuthUser; noAccess: false; email: string | null }
  | { loading: false; user: null; noAccess: true; email: string | null };

export function useAuth(): UseAuthResult {
  const [state, setState] = useState<UseAuthResult>({
    loading: true,
    user: null,
    noAccess: false,
    email: null,
  });

  useEffect(() => {
    const tok = readToken();
    if (tok.status === 'none') {
      redirectToLogin();
      return;
    }
    if (tok.status === 'no-access') {
      setState({ loading: false, user: null, noAccess: true, email: tok.email });
      return;
    }
    setState({ loading: false, user: tok.user!, noAccess: false, email: tok.email });
  }, []);

  return state;
}

export function getToken(): string | null {
  return getCookie('rw_token');
}
